import { api, ApiError } from '../api.js'
import { isHistoryInstant } from '../dateTime/historyDateTime.js'
import { paginationItems } from './transactions.js'

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const pageSizes = new Set([25, 50, 100])
const completenessModes = new Set(['ALL', 'COMPLETE', 'INCOMPLETE'])
const facets = new Set(['TANK', 'FUEL'])
const timeQualities = new Set(['KNOWN', 'MISSING', 'AMBIGUOUS', 'INVALID'])
const displayStates = new Set(['NORMAL', 'INCOMPLETE'])
const transports = new Set(['HTTP', 'HTTPS', 'WS', 'WSS'])
const maximumRangeMilliseconds = 366 * 24 * 60 * 60 * 1000
const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
const invalid = (kind = 'response') => new ApiError(`Invalid PTS-2 delivery ${kind}`,
  kind === 'query' ? 400 : 502, `pts2_delivery_${kind}_invalid`)

function exact(value, fields) {
  return isRecord(value) && Object.keys(value).length === fields.length
    && fields.every((field) => Object.hasOwn(value, field))
}

function positive(value, maximum = Number.MAX_SAFE_INTEGER) {
  const number = Number(value)
  if (!Number.isSafeInteger(number) || number < 1 || number > maximum) throw invalid('query')
  return number
}

function integerSet(value, { minimum, maximum, length }) {
  if (value == null) return []
  if (!Array.isArray(value) || value.length > length) throw invalid('query')
  const result = value.map(Number)
  if (result.some((item) => !Number.isSafeInteger(item) || item < minimum || item > maximum)
    || new Set(result).size !== result.length) throw invalid('query')
  return result
}

function iso(value, exclusiveEnd = false) {
  const date = new Date(value)
  if (!isHistoryInstant(value, { exclusiveEnd })) throw invalid('query')
  return date.toISOString()
}

function instantOrNull(value) {
  return value === null || typeof value === 'string' && value.length > 0 && Number.isFinite(Date.parse(value))
}

function textOrNull(value, maximum = Number.MAX_SAFE_INTEGER) {
  return value === null || typeof value === 'string' && value.length <= maximum
}

function decimal(value) {
  return value === null || typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value)
}

function nonNegativeIntegerOrNull(value) {
  return value === null || Number.isSafeInteger(value) && value >= 0
}

export function deliveryRangeIssue(from, to) {
  const start = Date.parse(from), end = Date.parse(to)
  if (!isHistoryInstant(from) || !isHistoryInstant(to, { exclusiveEnd: true })) return 'REQUIRED'
  if (start >= end) return 'ORDER'
  if (end - start > maximumRangeMilliseconds) return 'TOO_LONG'
  return null
}

export function normalizeDeliveryFilters(value = {}) {
  const result = {
    stationId: positive(value.stationId),
    controllerId: positive(value.controllerId),
    from: iso(value.from),
    to: iso(value.to, true),
    tankNumbers: integerSet(value.tankNumbers, { minimum: 0, maximum: 20, length: 21 }),
    fuelGradeIds: integerSet(value.fuelGradeIds, { minimum: 1, maximum: 20, length: 20 }),
    completeness: value.completeness || 'ALL',
  }
  if (!completenessModes.has(result.completeness) || deliveryRangeIssue(result.from, result.to)) throw invalid('query')
  return result
}

export function filterPayload(value) {
  const { completeness, ...filters } = normalizeDeliveryFilters(value)
  const result = Object.fromEntries(Object.entries(filters).filter(([, item]) => !(Array.isArray(item) && item.length === 0)))
  if (completeness !== 'ALL') result.completenessMode = completeness
  return result
}

function queryFor(filters, page, limit) {
  page = positive(page, 10000)
  limit = Number(limit)
  if (!pageSizes.has(limit)) throw invalid('query')
  const payload = filterPayload(filters)
  const query = new URLSearchParams(Object.fromEntries(Object.entries(payload).filter(([, value]) => !Array.isArray(value))))
  for (const field of ['tankNumbers', 'fuelGradeIds']) if (payload[field]?.length) query.set(field, payload[field].join(','))
  query.set('page', String(page)); query.set('limit', String(limit))
  return query
}

const rowFields = ['id', 'stationId', 'controllerId', 'eventAt', 'eventTimeLocal', 'eventTimeQuality', 'startedAt',
  'startTimeLocal', 'startTimeQuality', 'stationTimeZone', 'receivedAt', 'durationSeconds', 'tankNumber',
  'fuelGradeId', 'fuelGradeName', 'startProductVolume', 'endProductVolume', 'productVolume', 'pumpsDispensedVolume',
  'dataComplete', 'displayState']

function deliveryRow(value, stationId, controllerId) {
  if (!exact(value, rowFields) || !uuid.test(value.id) || value.stationId !== stationId || value.controllerId !== controllerId
    || !instantOrNull(value.eventAt) || !textOrNull(value.eventTimeLocal) || !timeQualities.has(value.eventTimeQuality)
    || !instantOrNull(value.startedAt) || !textOrNull(value.startTimeLocal) || !timeQualities.has(value.startTimeQuality)
    || typeof value.stationTimeZone !== 'string' || !value.stationTimeZone || !instantOrNull(value.receivedAt)
    || !nonNegativeIntegerOrNull(value.durationSeconds)
    || !(value.tankNumber === null || Number.isSafeInteger(value.tankNumber) && value.tankNumber >= 0 && value.tankNumber <= 20)
    || !(value.fuelGradeId === null || Number.isSafeInteger(value.fuelGradeId) && value.fuelGradeId >= 1 && value.fuelGradeId <= 20)
    || !textOrNull(value.fuelGradeName, 20)
    || !['startProductVolume', 'endProductVolume', 'productVolume', 'pumpsDispensedVolume'].every((field) => decimal(value[field]))
    || typeof value.dataComplete !== 'boolean' || !displayStates.has(value.displayState)
    || value.dataComplete !== (value.displayState === 'NORMAL')) throw invalid()
  return structuredClone(value)
}

const summaryFields = ['all', 'productVolume', 'productVolumeKnown', 'pumpsDispensedVolume', 'pumpsDispensedKnown', 'incomplete']
function summary(value) {
  if (!exact(value, summaryFields)
    || !['all', 'productVolumeKnown', 'pumpsDispensedKnown', 'incomplete']
      .every((field) => Number.isSafeInteger(value[field]) && value[field] >= 0)
    || !decimal(value.productVolume) || !decimal(value.pumpsDispensedVolume)
    || value.productVolumeKnown > value.all || value.pumpsDispensedKnown > value.all || value.incomplete > value.all) throw invalid()
  return structuredClone(value)
}

export async function getPts2Deliveries(filters, { page = 1, limit = 25, signal } = {}) {
  filters = normalizeDeliveryFilters(filters)
  const value = await api(`/pts2/in-tank-deliveries/browse?${queryFor(filters, page, limit)}`, { signal })
  if (!exact(value, ['items', 'page', 'limit', 'totalItems', 'totalPages', 'summary'])
    || !Array.isArray(value.items) || value.items.length > limit
    || !Number.isSafeInteger(value.page) || value.page < 1 || value.limit !== Number(limit)
    || !Number.isSafeInteger(value.totalItems) || value.totalItems < 0
    || !Number.isSafeInteger(value.totalPages) || value.totalPages < 0) throw invalid()
  return { ...value, summary: summary(value.summary),
    items: value.items.map((item) => deliveryRow(item, filters.stationId, filters.controllerId)) }
}

export async function getPts2DeliveryOptions(filters, facet, { signal } = {}) {
  const payload = filterPayload(filters)
  if (!facets.has(facet)) throw invalid('query')
  const value = await api('/pts2/in-tank-deliveries/filter-options', {
    method: 'POST', signal, body: JSON.stringify({ filters: payload, facet }),
  })
  const maximum = facet === 'TANK' ? 21 : 20
  const minimumNumber = facet === 'TANK' ? 0 : 1
  if (!exact(value, ['facet', 'items', 'hasUnassigned']) || value.facet !== facet
    || !Array.isArray(value.items) || value.items.length > maximum || typeof value.hasUnassigned !== 'boolean'
    || value.items.some((item) => !exact(item, ['number', 'name']) || !Number.isSafeInteger(item.number)
      || item.number < minimumNumber || item.number > 20 || !textOrNull(item.name, facet === 'FUEL' ? 20 : Number.MAX_SAFE_INTEGER))) throw invalid()
  return structuredClone(value)
}

const timedFields = ['dateTimeLocal', 'timeQuality', 'productHeight', 'waterHeight', 'temperature', 'productVolume',
  'productTcVolume', 'productDensity', 'productMass']
const absoluteFields = ['productHeight', 'waterHeight', 'temperature', 'productVolume', 'productTcVolume',
  'productDensity', 'productMass', 'pumpsDispensedVolume']

function timedValues(value) {
  return exact(value, timedFields) && textOrNull(value.dateTimeLocal) && timeQualities.has(value.timeQuality)
    && timedFields.slice(2).every((field) => decimal(value[field]))
}

function absoluteValues(value) {
  return exact(value, absoluteFields) && absoluteFields.every((field) => decimal(value[field]))
}

export async function getPts2DeliveryDetail(stationId, controllerId, deliveryId, { signal } = {}) {
  stationId = positive(stationId); controllerId = positive(controllerId)
  if (!uuid.test(deliveryId)) throw invalid('query')
  const value = await api(`/pts2/in-tank-deliveries/${deliveryId}?stationId=${stationId}&controllerId=${controllerId}`, { signal })
  const fields = ['delivery', 'startValues', 'endValues', 'absoluteValues', 'deliveriesUploaded', 'deliveriesTotal',
    'configurationId', 'transport', 'packetType', 'stationName', 'controllerName']
  if (!exact(value, fields) || !timedValues(value.startValues) || !timedValues(value.endValues)
    || !absoluteValues(value.absoluteValues) || !nonNegativeIntegerOrNull(value.deliveriesUploaded)
    || !nonNegativeIntegerOrNull(value.deliveriesTotal)
    || !(value.configurationId === null || typeof value.configurationId === 'string' && /^[0-9A-F]{1,8}$/.test(value.configurationId))
    || !transports.has(value.transport) || value.packetType !== 'UploadInTankDelivery'
    || !textOrNull(value.stationName, 128) || !textOrNull(value.controllerName, 64)) throw invalid()
  value.delivery = deliveryRow(value.delivery, stationId, controllerId)
  if (value.delivery.id !== deliveryId) throw invalid()
  return structuredClone(value)
}

export { paginationItems }
